"use client";

import React, { ReactNode, useState, useRef } from "react";
import { useParams, usePathname } from "next/navigation";
import Image from "next/image";
import DownIcon from "@public/common/svg/chevron-down.svg";
import cx from "classnames";
import useRedirect from "@utils/useRedirect";
import useClickOutside from "@utils/useClickOutside";
import { useUserContext } from "@/context/UserContext";
import { Theme, HeaderVariant } from "@constants/enums";
import { EVENT_NAME } from "@constants/constants";

import AskDatamuniIcon from "@public/common/svg/ask-datamuni-logo.svg";
import DiscoverIcon from "@public/common/svg/globe-icon.svg";
import ExploreIcon from "@public/common/svg/compass-icon.svg";

import Modal from "@components/Modal/Modal";
import Auth from "@components/Auth/Auth";
import styles from "./Header.module.scss";

import NavigationFooter from "../NavigationFooter/NavigationFooter";
import useWindowDimensions from "@/utils/useWindowDimensions";

interface HeaderProp {
    variant?: HeaderVariant;
    classname?: string;
    children?: ReactNode;
    dataDisplay?: boolean;
    theme?: Theme;
    bottomNavBarShow?: boolean;
    userDetails?: { name: string; image?: string } | null;
}

const generateHeaderObject = (pathname, theme) => {
    return [
        {
            id: 1,
            location: "/ask-datamuni",
            name: "Ask Datamuni",
            click_event_name: EVENT_NAME.ASK_DATAMUNI_CLICK,
            icon: (
                <AskDatamuniIcon
                    className={
                        pathname === "/ask-datamuni" ? styles.activeLogo : ""
                    }
                    data-theme={theme}
                />
            ),
        },
        {
            id: 2,
            location: "/news",
            name: "Discover",
            click_event_name: EVENT_NAME.DISCOVER_CLICK,
            icon: (
                <DiscoverIcon
                    className={pathname === "/news" ? styles.activeLogo : ""}
                    data-theme={theme}
                />
            ),
        },
        {
            id: 3,
            location: `/explore`,
            name: "Explore",
            click_event_name: EVENT_NAME.EXPLORE_V2_CLICK,
            icon: (
                <ExploreIcon
                    className={
                        /^\/explore$|^\/wiki\//.test(pathname)
                            ? styles.activeLogo
                            : ""
                    }
                    data-theme={theme}
                />
            ),
        },
        {
            id: 4,
            location: "/about-us",
            name: "About Us",
            click_event_name: EVENT_NAME.ABOUT_US_CLICK,
        },
    ];
};

const Header: React.FC<HeaderProp> = ({
    variant = HeaderVariant.SOLID,
    classname,
    children: customChildren,
    dataDisplay,
    theme = Theme.LIGHT,
    bottomNavBarShow = true,
}) => {
    if (typeof window === "undefined") {
        return null;
    }
    const pathname = usePathname();
    const { handleRedirect } = useRedirect();
    const { user, handleLogout } = useUserContext();
    const dimensions = useWindowDimensions();

    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Custom Hooks
    useClickOutside(dropdownRef, () => {
        if (isDropdownOpen) {
            setIsDropdownOpen(false);
        }
    });

    const isActive = (path: string) => {
        if (path === "/explore") {
            return /^\/explore$|^\/wiki\//.test(pathname);
        }
        return pathname === path;
    };

    const toggleDropdown = () => setIsDropdownOpen((prevState) => !prevState);
    const handleAuthModalOpen = () => setIsAuthModalOpen(true);
    const handleAuthModalClose = () => setIsAuthModalOpen(false);

    return (
        <nav
            className={cx(styles.navBar, styles[`nav_${variant}`], classname)}
            data-theme={theme}
        >
            <div
                data-display={!dataDisplay}
                className={cx(styles.header, styles[`header_${variant}`])}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                    }}
                >
                    <Image
                        src={"/common/svg/datamuni-icon-color-light.svg"}
                        alt="DatamuniIcon"
                        width={dimensions.width < 640 ? 18 : 24}
                        height={dimensions.width < 640 ? 18 : 24}
                        onClick={() =>
                            handleRedirect({
                                path: "/",
                                event_name: EVENT_NAME.HOME_CLICK,
                                event_source: "Datamuni Icon",
                            })
                        }
                        className={styles.dataMuniIcon}
                        data-display={!dataDisplay}
                    />
                    <div
                        className={styles.datamuniHeaderTitle}
                        data-theme={theme}
                    >
                        Datamuni
                    </div>
                </div>
                <header
                    className={styles["right-container"]}
                    data-display={!dataDisplay}
                >
                    <article className={styles["tab-container"]}>
                        {generateHeaderObject(pathname, theme).map((item) => (
                            <div
                                key={item.id}
                                className={cx(styles["route-name"], {
                                    [styles["active"]]: isActive(item.location),
                                })}
                                data-theme={theme}
                                onClick={() =>
                                    handleRedirect({
                                        path: item.location,
                                        event_name: item.click_event_name,
                                        event_source: "Header Label",
                                    })
                                }
                            >
                                {item.icon}
                                {item.name}
                            </div>
                        ))}
                    </article>
                    <article
                        className={styles["children-container"]}
                        data-display={!dataDisplay}
                    >
                        {customChildren ?? (
                            <div className={styles["user-section"]}>
                                {user ? (
                                    <div
                                        className={styles["profile-container"]}
                                        ref={dropdownRef}
                                    >
                                        {user?.image_url ? (
                                            <div
                                                className={
                                                    styles["profile-info"]
                                                }
                                                onClick={() => toggleDropdown()}
                                            >
                                                <Image
                                                    src={user?.image_url}
                                                    alt="User Avatar"
                                                    loading="eager"
                                                    height={20}
                                                    width={20}
                                                    className={
                                                        styles[
                                                            "user-name-image"
                                                        ]
                                                    }
                                                />
                                                <p
                                                    className={
                                                        styles["username"]
                                                    }
                                                >
                                                    {user?.name}
                                                </p>
                                                <DownIcon />
                                            </div>
                                        ) : (
                                            <div
                                                className={
                                                    styles["profile-info"]
                                                }
                                                onClick={() => toggleDropdown()}
                                            >
                                                <div
                                                    className={
                                                        styles[
                                                            "user-name-container"
                                                        ]
                                                    }
                                                >
                                                    {user?.name[0]}
                                                </div>
                                                <p
                                                    className={
                                                        styles["username"]
                                                    }
                                                >
                                                    {user?.name}
                                                </p>
                                                <DownIcon />
                                            </div>
                                        )}
                                        {isDropdownOpen && (
                                            <div
                                                className={
                                                    styles["dropdown-menu"]
                                                }
                                                onClick={() => handleLogout()}
                                            >
                                                <Image
                                                    src={
                                                        "/common/svg/logout-icon.svg"
                                                    }
                                                    alt="LogoutIcon"
                                                    width={16}
                                                    height={16}
                                                />
                                                <p
                                                    className={
                                                        styles["logout-text"]
                                                    }
                                                >
                                                    Logout
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <Modal
                                            open={isAuthModalOpen}
                                            onClose={handleAuthModalClose}
                                            onOpen={handleAuthModalOpen}
                                            backdrop={true}
                                            modalTitle="Login"
                                            loginClassName={
                                                styles.loginClassName
                                            }
                                        >
                                            <Auth />
                                        </Modal>
                                    </div>
                                )}
                            </div>
                        )}
                    </article>
                </header>
            </div>
            {theme === Theme.LIGHT && bottomNavBarShow && <NavigationFooter />}
        </nav>
    );
};

export default Header;
